import React from 'react';
import { X, Users, Pencil, Calendar as CalendarIcon, User, Phone, Mail, FileText, Trash2, MessageSquare, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { LeadRecord } from '../data/mockData';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';
import { PipelineStatusBadge, DocumentStatusBadge } from './TableCells';
import { useState, useEffect, useCallback } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { CustomerValidDesignations } from '../../constants';

// ── Record Detail Drawer ────────────────────────────────────────────

interface AdminUserList {
  id: number;
  name: string;
};

interface RecordDetailDrawerProps {
  record: LeadRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdateRecord?: (id: string, updates: Partial<LeadRecord>) => void | Promise<void>;
  adminUserList?: AdminUserList[];

}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value);
}

function formatDate(date: Date): string {
  return format(date, 'MMM dd, yyyy');
}

function formatTime(date: Date): string {
  return format(date, 'hh:mm a');
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return formatDate(date);
}

type EditableField = 'afLead' | 'partnerOwner' | 'nextFollowup' | null;
type AfLeadOption = { id: string; name: string };
type PartnerCompanyOption = { id: string; name: string };
type ContactFormState = {
  customerName: string;
  phone: string;
  email: string;
  designation: string;
};
const getStringValue = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
const isValidDateValue = (value: unknown): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime());

const getInitials = (name: string) => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + (parts[parts.length - 1][0] || '')).toUpperCase();
};

const UserAvatar = ({ name, className, colorScheme = 'default' }: { name: string; className?: string; colorScheme?: 'default' | 'blue' | 'purple' | 'green' | 'orange' }) => {
  const schemes = {
    default: 'bg-slate-100 text-slate-600 border-slate-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    green: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
  };

  return (
    <div className={cn(
      "flex items-center justify-center rounded-full font-semibold border shrink-0 select-none",
      schemes[colorScheme],
      className
    )}>
      {getInitials(name) || '?'}
    </div>
  );
};

const normalizeAfLeadOptions = (payload: unknown): AfLeadOption[] => {
  const payloadObject = (payload && typeof payload === 'object') ? payload as Record<string, unknown> : undefined;
  const nestedData = (payloadObject?.data && typeof payloadObject.data === 'object')
    ? payloadObject.data as Record<string, unknown>
    : undefined;

  const rawList = Array.isArray(payload)
    ? payload
    : Array.isArray(payloadObject?.data)
      ? payloadObject.data
      : Array.isArray(payloadObject?.users)
        ? payloadObject.users
        : Array.isArray(payloadObject?.adminUsers)
          ? payloadObject.adminUsers
          : Array.isArray(nestedData?.users)
            ? nestedData.users
            : Array.isArray(nestedData?.adminUsers)
              ? nestedData.adminUsers
              : [];

  return rawList
    .map((item: unknown) => {
      if (!item || typeof item !== 'object') return null;
      const option = item as Record<string, unknown>;
      const name = getStringValue(option.leadRM)
        || getStringValue(option.name)
        || getStringValue(option.fullName)
        || getStringValue(option.user_name);
      const idValue = option.id ?? option.user_id ?? option.af_lead_id ?? option.lead_rm_id;
      const id = idValue === undefined || idValue === null ? '' : String(idValue).trim();
      if (!name || !id) return null;
      return { id, name };
    })
    .filter((option: AfLeadOption | null): option is AfLeadOption => option !== null);
};

const normalizePartnerOptions = (payload: unknown): PartnerCompanyOption[] => {
  const payloadObject = (payload && typeof payload === 'object') ? payload as Record<string, unknown> : undefined;
  const nestedData = (payloadObject?.data && typeof payloadObject.data === 'object')
    ? payloadObject.data as Record<string, unknown>
    : undefined;

  const rawList = Array.isArray(payload)
    ? payload
    : Array.isArray(payloadObject?.data)
      ? payloadObject.data
      : Array.isArray(payloadObject?.partners)
        ? payloadObject.partners
        : Array.isArray(payloadObject?.partnerCompanies)
          ? payloadObject.partnerCompanies
          : Array.isArray(payloadObject?.partner_company_list)
            ? payloadObject.partner_company_list
            : Array.isArray(nestedData?.partners)
              ? nestedData.partners
              : Array.isArray(nestedData?.partnerCompanies)
                ? nestedData.partnerCompanies
                : Array.isArray(nestedData?.partner_company_list)
                  ? nestedData.partner_company_list
                  : [];

  return rawList
    .map((item: unknown) => {
      if (!item || typeof item !== 'object') return null;
      const option = item as Record<string, unknown>;
      const name = getStringValue(option.partnerCompanyName)
        || getStringValue(option.partner_company_name)
        || getStringValue(option.name)
        || getStringValue(option.companyName);
      const idValue = option.partner_id ?? option.partnerId ?? option.id ?? option.company_id;
      const id = idValue === undefined || idValue === null ? '' : String(idValue).trim();
      if (!name || !id) return null;
      return { id, name };
    })
    .filter((option: PartnerCompanyOption | null): option is PartnerCompanyOption => option !== null);
};

const getContactFormFromRecord = (record: LeadRecord): ContactFormState => ({
  customerName: record.customerName ?? '',
  phone: record.phone ?? '',
  email: record.email ?? '',
  designation: record.designation ?? '',
});

export function RecordDetailDrawer({ record, isOpen, onClose, onUpdateRecord, adminUserList }: RecordDetailDrawerProps) {

  const [editingField, setEditingField] = useState<EditableField>(null);
  const [tempValue, setTempValue] = useState<string | Date | null>(null);
  const [isUpdatingDate, setIsUpdatingDate] = useState(false);
  const [afLeadOptions, setAfLeadOptions] = useState<AfLeadOption[]>([]);
  const [partnerOptions, setPartnerOptions] = useState<PartnerCompanyOption[]>([]);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [contactForm, setContactForm] = useState<ContactFormState>({
    customerName: '',
    phone: '',
    email: '',
    designation: '',
  });
  const [isSavingContact, setIsSavingContact] = useState(false);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) onClose();
  }, [isOpen, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const fetchAfLeads = async () => {
      if (!isOpen) {
        return;
      }

      try {
        const response = await apiService.get('/customers/get-admin-user-list-active') as { data?: unknown };
        setAfLeadOptions(normalizeAfLeadOptions(response?.data));
      } catch (error) {
        console.error('Error fetching AF lead list:', error);
      }
    };

    void fetchAfLeads();
  }, [isOpen]);

  useEffect(() => {
    const fetchPartnerCompanies = async () => {
      if (!isOpen) {
        return;
      }

      try {
        const response = await apiService.get('/customers/get-partner-company-list') as { data?: unknown };
        const options = normalizePartnerOptions(response?.data);

        setPartnerOptions(options);
      } catch (error) {
        console.error('Error fetching partner companies:', error);
      }
    };

    void fetchPartnerCompanies();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsEditingContact(false);
      setIsSavingContact(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!record) {
      return;
    }

    setIsEditingContact(false);
    setContactForm(getContactFormFromRecord(record));
  }, [record]);

  if (!record) return null;

  const handleEdit = (field: EditableField, currentValue: string | Date | null) => {
    setEditingField(field);
    setTempValue(currentValue);
  };

  const handleDateChange = async (selectedDate: Date | null) => {
    if (!onUpdateRecord) {
      setEditingField(null);
      setTempValue(null);
      return;
    }

    setTempValue(selectedDate);
    setIsUpdatingDate(true);

    try {
      await Promise.resolve(onUpdateRecord(record.id, {
        next_follow_up_date: selectedDate ?? undefined,
      }));
    } finally {
      setIsUpdatingDate(false);
      setEditingField(null);
      setTempValue(null);
    }
  };

  const handleCancel = () => { setEditingField(null); setTempValue(null); };

  const handleEditContact = () => {
    setContactForm(getContactFormFromRecord(record));
    setIsEditingContact(true);
  };

  const handleCancelContactEdit = () => {
    setContactForm(getContactFormFromRecord(record));
    setIsEditingContact(false);
  };

  const handleContactFieldChange = (field: keyof ContactFormState, value: string) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveContact = async () => {
    setIsSavingContact(true);

    try {
      await apiService.put('/customers/update-customer-details', {
        customer_id: record.id,
        customer_name: contactForm.customerName.trim(),
        phone: contactForm.phone.trim(),
        email: contactForm.email.trim(),
        designation: contactForm.designation,
      });

      if (onUpdateRecord) {
        onUpdateRecord(record.id, {
          customerName: contactForm.customerName.trim(),
          phone: contactForm.phone.trim(),
          email: contactForm.email.trim(),
          designation: contactForm.designation,
        });
      }

      setIsEditingContact(false);
      toast.success('Contact details updated successfully.');
    } catch (error) {
      console.error('Error updating contact details:', error);
      toast.error('Failed to update contact details.');
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleAfLeadChange = async (afLeadId: string) => {
    const selectedAfLead = afLeadOptions.find((user) => user.id === afLeadId);

    try {
      await apiService.put('/customers/update-customer-af-lead', {
        customer_id: record.id,
        af_lead_id: afLeadId,
      });

      if (onUpdateRecord) {
        onUpdateRecord(record.id, { leadRM: selectedAfLead?.id ?? '' });
      }
      setEditingField(null);
      setTempValue(null);
      toast.success('AF Lead updated successfully.');
    } catch (error) {
      console.error('Error updating AF Lead:', error);
      toast.error('Failed to update AF Lead.');
    }
  };

  const handleAfLeadDelete = async () => {
    try {
      await apiService.put('/customers/update-customer-af-lead', {
        customer_id: record.id,
        af_lead_id: ' ',
      });

      if (onUpdateRecord) {
        onUpdateRecord(record.id, { leadRM: '' });
      }
      setEditingField(null);
      setTempValue(null);
      toast.success('AF Lead removed successfully.');
    } catch (error) {
      console.error('Error removing AF Lead:', error);
      toast.error('Failed to remove AF Lead.');
    }
  };

  const handlePartnerChange = async (partnerId: string) => {
    const selectedPartner = partnerOptions.find((partner) => partner.id === partnerId);

    try {
      await apiService.put('/customers/update-customer-partner-id', {
        customer_id: record.id,
        partner_id: partnerId,
      });

      if (onUpdateRecord) {
        onUpdateRecord(record.id, { partnerCompanyName: selectedPartner?.name ?? '' });
      }
      setEditingField(null);
      setTempValue(null);
      toast.success('Partner updated successfully.');
    } catch (error) {
      console.error('Error updating customer partner:', error);
      toast.error('Failed to update partner.');
    }
  };

  const handlePartnerDelete = async () => {
    try {
      await apiService.put('/customers/update-customer-partner-id', {
        customer_id: record.id,
        partner_id: ' ',
      });

      if (onUpdateRecord) {
        onUpdateRecord(record.id, { partnerCompanyName: '' });
      }
      setEditingField(null);
      setTempValue(null);
      toast.success('Partner removed successfully.');
    } catch (error) {
      console.error('Error removing customer partner:', error);
      toast.error('Failed to remove partner.');
    }
  };

  const handleDeleteDate = async () => {
    if (!onUpdateRecord) {
      setEditingField(null);
      setTempValue(null);
      return;
    }

    setIsUpdatingDate(true);

    try {
      await Promise.resolve(onUpdateRecord(record.id, { next_follow_up_date: undefined }));
    } finally {
      setIsUpdatingDate(false);
      setEditingField(null);
      setTempValue(null);
    }
  };

  const EditableAfLeadField = ({ value }: { value: string }) => {
    const isEditing = editingField === 'afLead';
    // const hasValue = value && value?.trim() !== '';
    const hasValue =
      typeof value === 'string'
        ? value.trim() !== ''
        : typeof value === 'object' && value !== null
          ? Boolean(value)
          : false;
    const selectedAfLeadId = afLeadOptions.find((option) => option.name === value)?.id ?? '';

    return (
      <div className="flex justify-between items-center py-2 px-2.5 rounded-lg group hover:bg-muted/40 transition-colors">
        <div className="flex items-center gap-2">
          <UserAvatar name={value} colorScheme="blue" className="w-6 h-6 text-[10px]" />
          <span className="text-xs text-muted-foreground">AF Lead</span>
        </div>
        {isEditing ? (
          <div className="flex items-center gap-1.5">
            <select
              value={selectedAfLeadId}
              onChange={(e) => {
                const nextAfLeadId = e.target.value;
                if (nextAfLeadId) {
                  void handleAfLeadChange(nextAfLeadId);
                }
              }}
              className="h-8 w-[180px] rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/10"
            >
              <option value="">Select...</option>
              {afLeadOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
              <X className="h-3 w-3 text-muted-foreground" />
            </Button>
          </div>
        ) : hasValue ? (
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium">{value}</span>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleEdit('afLead', value)}>
                <Pencil className="h-3 w-3 text-muted-foreground" />
              </Button>
              {/* <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => void handleAfLeadDelete()}>
                <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
              </Button> */}
            </div>
          </div>
        ) : (
          <Button variant="ghost" className="h-7 text-xs text-muted-foreground hover:text-dashboard-accent px-2" onClick={() => handleEdit('afLead', '')}>
            <Pencil className="mr-1.5 h-3 w-3" /> Assign
          </Button>
        )}
      </div>
    );
  };

  const EditableDateField = ({ label, value }: { label: string; value?: Date }) => {
    const isEditing = editingField === 'nextFollowup';
    const currentDate = isValidDateValue(value) ? value : null;
    const hasValue = currentDate !== null;
    const selectedDate = isValidDateValue(tempValue) ? tempValue : null;

    return (
      <div className="rounded-lg border border-border/60 bg-muted/30 p-3 transition-colors hover:bg-muted/40">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">{label}</p>
            <div className="mt-2 flex items-center gap-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background text-dashboard-accent ring-1 ring-border/60">
                {isUpdatingDate ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CalendarIcon className="h-4 w-4" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  {hasValue ? formatDate(currentDate) : 'No date selected'}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {hasValue ? 'Saved follow-up date' : 'Pick a date and it will save immediately'}
                </p>
              </div>
            </div>
          </div>

          {isEditing ? (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 shrink-0"
              onClick={handleCancel}
              disabled={isUpdatingDate}
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          ) : hasValue ? (
            <div className="flex items-center gap-0.5">
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => handleEdit('nextFollowup', currentDate)}
                disabled={isUpdatingDate}
              >
                <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => void handleDeleteDate()}
                disabled={isUpdatingDate}
              >
                <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-xs text-muted-foreground hover:text-dashboard-accent"
              onClick={() => handleEdit('nextFollowup', new Date())}
              disabled={isUpdatingDate}
            >
              Set date
            </Button>
          )}
        </div>

        {isEditing && (
          <div className="mt-3 space-y-2">
            <div className="relative">
              <CalendarIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <DatePicker
                selected={selectedDate}
                onChange={(date) => void handleDateChange(date)}
                dateFormat="dd MMM yyyy"
                placeholderText="Select next follow-up date"
                wrapperClassName="w-full"
                className="h-10 w-full rounded-md border border-border bg-background pl-10 pr-3 text-sm text-foreground outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/10 disabled:cursor-not-allowed disabled:opacity-60"
                calendarClassName="rounded-xl border border-border shadow-xl"
                popperClassName="z-[260]"
                shouldCloseOnSelect
                disabled={isUpdatingDate}
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] text-muted-foreground">
                {selectedDate ? `Selected: ${formatDate(selectedDate)}` : 'Choose a date to update it in the database'}
              </p>
              {hasValue && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                  onClick={() => void handleDeleteDate()}
                  disabled={isUpdatingDate}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const EditablePartnerField = ({ value }: { value: string }) => {
    const isEditing = editingField === 'partnerOwner';
    const hasValue = value && value.trim() !== '';
    const selectedPartnerId = partnerOptions.find((option) => option.name === value)?.id ?? '';

    return (
      <div className="flex justify-between items-center py-2 px-2.5 rounded-lg group hover:bg-muted/40 transition-colors">
        <div className="flex items-center gap-2">
          <UserAvatar name={value || 'Partner'} colorScheme="purple" className="w-6 h-6 text-[10px]" />
          <span className="text-xs text-muted-foreground">Partner</span>
        </div>
        {isEditing ? (
          <div className="flex items-center gap-1.5">
            <select
              value={selectedPartnerId}
              onChange={(e) => {
                const nextPartnerId = e.target.value;
                if (nextPartnerId) {
                  void handlePartnerChange(nextPartnerId);
                }
              }}
              className="h-8 w-[180px] rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/10"
            >
              <option value="">Select...</option>
              {partnerOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancel}>
              <X className="h-3 w-3 text-muted-foreground" />
            </Button>
          </div>
        ) : hasValue ? (
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium">{value}</span>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleEdit('partnerOwner', value)}>
                <Pencil className="h-3 w-3 text-muted-foreground" />
              </Button>
              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => void handlePartnerDelete()}>
                <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="ghost" className="h-7 text-xs text-muted-foreground hover:text-dashboard-accent px-2" onClick={() => handleEdit('partnerOwner', '')}>
            <Pencil className="mr-1.5 h-3 w-3" /> Assign
          </Button>
        )}
      </div>
    );
  };


  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/20 z-[200] transition-opacity duration-200',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        className={cn(
          'fixed right-0 top-0 h-full w-full max-w-[26rem] bg-white z-[201] flex flex-col border-l border-slate-200',
          'shadow-[-8px_0_24px_rgba(15,23,42,0.07)] transition-transform duration-200 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0 bg-white">
          <div className="flex items-center gap-3 min-w-0">
            {record.companyLogo ? (
              <img
                src={record.companyLogo}
                alt={record.companyName}
                className="w-10 h-10 rounded-lg flex-shrink-0 bg-slate-50 object-contain border border-slate-100"
              />
            ) : (
              <UserAvatar
                name={record.companyName}
                className="w-10 h-10 rounded-lg text-sm"
                colorScheme="default"
              />
            )}
            <div className="min-w-0">
              <h3 className="font-semibold text-[14px] text-slate-800 leading-tight truncate">{record.companyName}</h3>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                {record.rating && (
                  <span className="text-[11px] text-slate-400 font-medium">{record.rating}</span>
                )}
                {record.city && (
                  <span className="text-[11px] text-slate-400">{record.city}</span>
                )}
              </div>
            </div>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="rounded-full h-8 w-8 text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Close <kbd className="ml-1 px-1 py-0.5 text-[10px] bg-muted rounded">Esc</kbd></p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* ── Key metrics strip ── */}
        <div className="flex items-center divide-x divide-slate-100 border-b border-slate-100 bg-slate-50 flex-shrink-0">
          <div className="flex-1 px-5 py-3">
            <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Deal Value</p>
            <p className="text-base font-bold text-slate-800">{formatCurrency(record.dealSize)}</p>
          </div>
          {record.productTypeList?.length > 0 && (
            <div className="flex-1 px-5 py-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Products</p>
              <div className="flex flex-wrap gap-1">
                {record.productTypeList.map((product, i) => (
                  <span key={i} className="px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded text-[10px] font-medium">
                    {product}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Scrollable body ── */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="py-4 px-5 space-y-5">

            {/* Status */}
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Status</p>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { label: 'MRA', node: <DocumentStatusBadge status={record.mraStatus} /> },
                  { label: 'TS Signed', node: <DocumentStatusBadge status={record.termSheetStatus} /> },
                  { label: 'Pipeline', node: <PipelineStatusBadge status={record.pipeline} /> },
                ] as { label: string; node: React.ReactNode }[]).map(({ label, node }) => (
                  <div key={label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                    <p className="text-[10px] text-slate-400 mb-1">{label}</p>
                    {node}
                  </div>
                ))}
              </div>
            </div>

            {/* Team */}
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Team</p>
              <div className="rounded-lg border border-slate-100 overflow-hidden divide-y divide-slate-100">
                <EditableAfLeadField value={adminUserList?.find((admin) => String(admin.id) === String(record.leadRM))?.name ?? ''} />
                <EditablePartnerField value={record.partnerCompanyName} />
              </div>
            </div>

            {/* Details */}
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Details</p>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                  <p className="text-[10px] text-slate-400 mb-1">City</p>
                  <p className="text-[13px] font-medium text-slate-700">{record.city || '—'}</p>
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                  <p className="text-[10px] text-slate-400 mb-1">Created Date</p>
                  <p className="text-[13px] font-medium text-slate-700">{record.createdDate || '—'}</p>
                </div>
              </div>
              <EditableDateField label="Next Followup" value={record.next_follow_up_date} />
            </div>

            {/* Lead Source */}
            {record.source && (
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Lead Source</p>
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5 flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <div>
                    <p className="text-[13px] font-medium text-slate-700">{record.source}</p>
                    {record.source === 'Past Relationship' && record.customerName && (
                      <p className="text-[11px] text-slate-400 mt-0.5">via <span className="text-slate-500">{record.customerName}</span></p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Contact */}
            {(record.customerName || record.email || record.phone || record.designation || isEditingContact) && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Contact</p>
                  {!isEditingContact && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-slate-400 hover:text-slate-600"
                      onClick={handleEditContact}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-3">
                  {isEditingContact ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Name</label>
                        <Input
                          value={contactForm.customerName}
                          onChange={(e) => handleContactFieldChange('customerName', e.target.value)}
                          placeholder="Customer name"
                          className="mt-1 h-9 text-[13px]"
                          disabled={isSavingContact}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Designation</label>
                        <select
                          value={contactForm.designation}
                          onChange={(e) => handleContactFieldChange('designation', e.target.value)}
                          className="mt-1 h-9 w-full rounded-md border border-border bg-background px-2 text-[13px] text-foreground outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/10 disabled:cursor-not-allowed disabled:opacity-60"
                          disabled={isSavingContact}
                        >
                          <option value="">Select designation</option>
                          {CustomerValidDesignations.map((designation) => (
                            <option key={designation} value={designation}>
                              {designation}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Phone</label>
                        <Input
                          value={contactForm.phone}
                          onChange={(e) => handleContactFieldChange('phone', e.target.value)}
                          placeholder="Phone number"
                          className="mt-1 h-9 text-[13px]"
                          disabled={isSavingContact}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Email</label>
                        <Input
                          type="email"
                          value={contactForm.email}
                          onChange={(e) => handleContactFieldChange('email', e.target.value)}
                          placeholder="Email address"
                          className="mt-1 h-9 text-[13px]"
                          disabled={isSavingContact}
                        />
                      </div>
                      <div className="flex items-center justify-end gap-2 pt-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 px-3 text-xs"
                          onClick={handleCancelContactEdit}
                          disabled={isSavingContact}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 px-3 text-xs"
                          onClick={() => void handleSaveContact()}
                          disabled={isSavingContact}
                        >
                          {isSavingContact ? (
                            <>
                              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                              Saving
                            </>
                          ) : (
                            'Save'
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {record.customerName && (
                        <div className="flex items-center gap-2.5 mb-2">
                          <UserAvatar name={record.customerName} className="w-8 h-8 text-[12px]" colorScheme="green" />
                          <div className="min-w-0">
                            <p className="text-[13px] font-semibold text-slate-700 truncate">{record.customerName}</p>
                            {record.designation && <p className="text-[11px] text-slate-400 truncate">{record.designation}</p>}
                          </div>
                        </div>
                      )}
                      <div className="flex flex-col gap-1.5">
                        {record.phone && (
                          <span className="flex items-center gap-1.5 text-[12px] text-slate-500">
                            <Phone className="w-3 h-3 text-slate-400" /> {record.phone}
                          </span>
                        )}
                        {record.email && (
                          <span className="flex items-center gap-1.5 text-[12px] text-slate-500 truncate">
                            <Mail className="w-3 h-3 text-slate-400" /> {record.email}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

          </div>
        </ScrollArea>
      </div>
    </>
  );
}

// ── Interaction Drawer (merged from InteractionDrawer.tsx) ──────────

interface InteractionDrawerProps {
  record: LeadRecord | null;
  isOpen: boolean;
  onClose: () => void;
}

export function InteractionDrawer({ record, isOpen, onClose }: InteractionDrawerProps) {
  if (!record) return null;

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 bg-foreground/20 backdrop-blur-sm z-[200] transition-opacity duration-150',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      <div
        className={cn(
          'fixed right-0 top-0 h-full w-full max-w-md bg-card shadow-drawer z-[201] transition-transform duration-150 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <img src={record.companyLogo} alt={record.companyName} className="w-10 h-10 rounded-full" />
            <div>
              <h3 className="font-semibold text-foreground">{record.companyName}</h3>
              <p className="text-sm text-muted-foreground">Interaction History</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <ScrollArea className="h-[calc(100vh-80px)]">
          <div className="p-6">
            <div className="mb-6 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4 text-dashboard-accent" />
                <span className="text-sm font-medium">{record.comments.length} Interactions</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Last updated {formatRelativeTime(new Date(record.comments[0]?.date) || new Date())}
              </p>
            </div>

            <div className="space-y-1">
              {record.comments.map((interaction, index) => (
                <InteractionItem key={interaction.id} interaction={interaction} isLast={index === record.comments.length - 1} />
              ))}
            </div>

            {record.comments.length === 0 && (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">No interactions recorded yet</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </>
  );
}

function InteractionItem({ interaction, isLast }: { interaction: any; isLast: boolean }) {
  return (
    <div className="relative pl-6 pb-6">
      {!isLast && <div className="absolute left-[11px] top-6 bottom-0 w-px bg-border" />}
      <div className="absolute left-0 top-1 w-[22px] h-[22px] rounded-full bg-accent/10 border-2 border-accent flex items-center justify-center">
        <div className="w-2 h-2 rounded-full bg-accent" />
      </div>
      <div className="bg-muted/30 rounded-lg p-4">
        <p className="text-sm text-foreground leading-relaxed mb-3">{interaction.comment}</p>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <User className="w-3.5 h-3.5" />
            <span>{interaction.user}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <CalendarIcon className="w-3.5 h-3.5" />
            <span>{formatDate(interaction.timestamp)}</span>
          </div>
          <span className="text-muted-foreground/70">{formatTime(interaction.timestamp)}</span>
        </div>
      </div>
    </div>
  );
}
